<script>
  import { createEventDispatcher } from 'svelte';
  import Modal from '../components/Modal.svelte';
  import { login, apiFetch } from '../lib/auth.js';

  export let open = false;

  const dispatch = createEventDispatcher();

  let mode = 'login'; // 'login' | 'signup'
  let email = '';
  let password = '';
  let username = '';
  let loading = false;
  let error = '';

  function close() {
    error = '';
    email = password = username = '';
    dispatch('close');
  }

  async function submit() {
    error = '';
    loading = true;
    try {
      if (mode === 'login') {
        await login(email, password);
        close();
      } else {
        await apiFetch('/auth/signup', {
          method: 'POST',
          body: JSON.stringify({ email, password, username }),
        });
        // auto-login after signup — backend login expects email field
        await login(email, password);
        close();
      }
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  function switchMode(m) {
    mode = m;
    error = '';
    email = password = username = '';
  }
</script>

<Modal title={mode === 'login' ? 'Login to see Unicorns' : 'Account creation for bastards'} {open} on:close={close}>
  <div class="auth-form">

    <div class="mode-toggle">
      <button class:active={mode === 'login'} on:click={() => switchMode('login')}>Login</button>
      <div class="divider"></div>
      <button class:active={mode === 'signup'} on:click={() => switchMode('signup')}>Sign Up</button>
    </div>

    {#if mode === 'signup'}
      <div class="info-box">
        <svg class="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <div class="info-text">
          <span class="info-title">No email verification yet</span>
          <span class="info-body">
            After signing up, DM us on Instagram to activate your account:
            <a class="info-link" href="https://instagram.com/deathmetaldiary" target="_blank" rel="noopener noreferrer">@deathmetaldiary</a>
          </span>
        </div>
      </div>
    {/if}

    {#if error}
      <div class="form-error">{error}</div>
    {/if}

    {#if mode === 'signup'}
      <div class="field">
        <label for="username">Username</label>
        <input id="username" type="text" bind:value={username} placeholder="your_alias" autocomplete="username" />
      </div>
    {/if}

    <div class="field">
      <label for="email">Email</label>
      <input id="email" type="email" bind:value={email} placeholder="you@domain.com" autocomplete="email" />
    </div>

    <div class="field">
      <label for="password">Password</label>
      <input id="password" type="password" bind:value={password} placeholder="••••••••" autocomplete={mode === 'login' ? 'current-password' : 'new-password'} />
    </div>

    <button class="btn-primary" on:click={submit} disabled={loading}>
      {loading ? 'LOADING...' : mode === 'login' ? 'ENTER' : 'SUMMON ACCOUNT'}
    </button>

    <p class="switch-hint">
      {mode === 'login' ? 'No account?' : 'Already initiated?'}
      <button class="link-btn" on:click={() => switchMode(mode === 'login' ? 'signup' : 'login')}>
        {mode === 'login' ? 'Sign up' : 'Log in'}
      </button>
    </p>

  </div>
</Modal>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600;700&display=swap');

  .auth-form { display: flex; flex-direction: column; gap: 0; }

  .mode-toggle {
    display: flex;
    align-items: center;
    gap: 0;
    margin-bottom: 1.25rem;
    border: 1px solid #1e3d00;
    border-radius: 2px;
    overflow: hidden;
  }

  .mode-toggle button {
    flex: 1;
    background: none;
    border: none;
    color: #444;
    font-family: 'Rajdhani', sans-serif;
    font-weight: 700;
    font-size: 0.6rem;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    padding: 0.6rem;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
  }

  .mode-toggle button.active {
    background: #1a3300;
    color: #6db800;
  }

  .mode-toggle .divider {
    width: 1px;
    height: 2rem;
    background: #1e3d00;
  }

  /* ── Info box ── */
  .info-box {
    display: flex;
    gap: 0.65rem;
    align-items: flex-start;
    background: #0d1a00;
    border: 1px solid #3a6b00;
    border-left: 3px solid #6db800;
    border-radius: 2px;
    padding: 0.7rem 0.75rem;
    margin-bottom: 1.25rem;
    box-shadow: 0 0 12px rgba(74,124,0,0.1);
  }

  .info-icon {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    margin-top: 1px;
    color: #6db800;
    filter: drop-shadow(0 0 4px rgba(109,184,0,0.5));
  }

  .info-text {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .info-title {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 700;
    font-size: 0.6rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #6db800;
  }

  .info-body {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 600;
    font-size: 0.7rem;
    color: #5a8a00;
    line-height: 1.4;
    letter-spacing: 0.03em;
  }

  .info-link {
    color: #8fd900;
    text-decoration: none;
    font-weight: 700;
    letter-spacing: 0.05em;
    transition: color 0.2s, text-shadow 0.2s;
  }

  .info-link:hover {
    color: #aef233;
    text-shadow: 0 0 6px rgba(109,184,0,0.6);
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-bottom: 1rem;
  }

  .field label {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 600;
    font-size: 0.55rem;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: #3a6b00;
  }

  .field input {
    background: #0a0a0a;
    border: 1px solid #1e3d00;
    border-radius: 2px;
    color: #c8c8c8;
    font-family: 'Rajdhani', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    padding: 0.6rem 0.75rem;
    width: 100%;
    transition: border-color 0.2s, box-shadow 0.2s;
    outline: none;
  }

  .field input:focus {
    border-color: #3a6b00;
    box-shadow: 0 0 0 2px rgba(74,124,0,0.15);
  }

  .form-error {
    background: rgba(204,68,0,0.1);
    border: 1px solid rgba(204,68,0,0.3);
    border-radius: 2px;
    color: #cc6633;
    font-family: 'Rajdhani', sans-serif;
    font-size: 0.65rem;
    letter-spacing: 0.1em;
    padding: 0.6rem 0.75rem;
    margin-bottom: 1rem;
    text-transform: uppercase;
  }

  .btn-primary {
    background: #1a3300;
    border: 1px solid #3a6b00;
    color: #6db800;
    font-family: 'Rajdhani', sans-serif;
    font-weight: 700;
    font-size: 0.65rem;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    border-radius: 2px;
    width: 100%;
    transition: background 0.2s, box-shadow 0.2s, color 0.2s;
    margin-bottom: 1rem;
  }

  .btn-primary:hover {
    background: #223d00;
    box-shadow: 0 0 12px rgba(109,184,0,0.3);
    color: #8fd900;
  }

  .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }

  .switch-hint {
    text-align: center;
    font-family: 'Rajdhani', sans-serif;
    font-size: 0.65rem;
    letter-spacing: 0.1em;
    color: #444;
    text-transform: uppercase;
  }

  .link-btn {
    background: none;
    border: none;
    color: #3a6b00;
    cursor: pointer;
    font-family: 'Rajdhani', sans-serif;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    text-decoration: underline;
    padding: 0;
    margin-left: 0.25rem;
    transition: color 0.2s;
  }

  .link-btn:hover { color: #6db800; }
</style>
